<script>
  import { router, useForm } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import FileButton from '../../components/file_button.svelte'
  import ScheduleEditor from '../../components/schedule_editor.svelte'
  import MultiSelect from '../../components/multi_select.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import { Button } from '../../components/ui'
  import AudienceUserList from '../../components/audience_user_list.svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'

  let { feed, options = {}, form_rules = {}, back_url = '/feeds' } = $props()

  const isEdit = !!feed.id

  // Plain field values under `feed`; the controller shapes targeting join rows and the
  // schedule's `_replace` keys, and validates (Precognition on change).
  // Hidden sections are sent as stored, like the old CMS, so switching type/audience never
  // deletes them.
  const form = useForm(isEdit ? 'patch' : 'post', isEdit ? `/feeds/${feed.id}` : '/feeds', {
    // the filtered list this form was opened from; the save lands back on it
    return_to: back_url,
    feed: {
      name: feed.name ?? '',
      display_name: feed.display_name ?? '',
      active: feed.active ?? true,
      manual_ranking_enabled: feed.manual_ranking_enabled ?? false,
      area_id: feed.area_id ?? '',
      feed_type: feed.feed_type ?? 'standard',
      impacted_places: feed.impacted_places ?? 'impacted_places_all',
      multiplier: feed.multiplier ?? null,
      audience_names: [...(feed.audience_names ?? ['everyone'])],
      user_ids: [...(feed.user_ids ?? [])],
      category_ids: [...(feed.category_ids ?? [])],
      sub_category_ids: [...(feed.sub_category_ids ?? [])],
      place_ids: [...(feed.place_ids ?? [])],
      schedule_attributes: feed.schedule_attributes
    }
  })

  liveValidate(form, 'feed')
  const errorFor = (name) => $form.errors[`feed.${name}`]

  // Which sections show, the impacted-places choices and the College Town's time zone come
  // from the server (`form_rules`), refreshed by a partial reload when type or town changes;
  // a town change also drops the places the new town doesn't have.
  const refreshRules = () =>
    router.reload({
      only: ['form_rules'],
      data: { feed_type: $form.feed.feed_type, area_id: $form.feed.area_id, place_ids: $form.feed.place_ids },
      onSuccess: (page) => {
        const placeIds = page.props.form_rules?.place_ids
        if (placeIds) $form.feed.place_ids = placeIds
      }
    })
  $effect(() => {
    if (form_rules.impacted_places && form_rules.impacted_places !== $form.feed.impacted_places) {
      $form.feed.impacted_places = form_rules.impacted_places
    }
  })
  // The schedule's visible zone follows the town (old CMS FeedForm); the admin can still change it.
  let appliedZone = form_rules.time_zone
  $effect(() => {
    if (!form_rules.time_zone || form_rules.time_zone === appliedZone) return
    appliedZone = form_rules.time_zone
    $form.feed.schedule_attributes.time_zone = form_rules.time_zone
  })

  const audienceType = $derived($form.feed.audience_names.includes('everyone') ? 'everyone' : 'custom')
  const setAudienceType = (type) => {
    const names = $form.feed.audience_names
    $form.feed.audience_names = type === 'everyone' ? ['everyone'] : names.filter((name) => name !== 'everyone')
  }
  const hasCsv = $derived($form.feed.audience_names.includes('uploaded_set'))

  // The server parses a picked CSV and merges its users into the list (csv_audience prop).
  let usersOpen = $state(false)
  let csv = $state({ summary: '', error: '' })

  const pickCsv = (event) => {
    const input = event.currentTarget
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    router.post('/feeds/csv_audience', { csv: file, user_ids: $form.feed.user_ids }, {
      only: ['csv_audience'],
      preserveState: true,
      preserveScroll: true,
      preserveUrl: true,
      onStart: () => (csv = { summary: '', error: '' }),
      onSuccess: (page) => {
        const result = page.props.csv_audience
        if (!result) return
        $form.feed.user_ids = result.user_ids
        csv = { ...csv, summary: result.summary }
      },
      onError: (errors) => (csv = { ...csv, error: errors['feed.users_csv'] ?? 'The import failed.' })
    })
  }
  const clearUsers = () => {
    $form.feed.user_ids = []
    csv = { summary: '', error: '' }
  }

  const submit = () => $form.submit()
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit Feed' : 'New Feed'} subtitle="Manually ranked content feeds per college town" />

  <FormLayout
    onsubmit={(e) => {
      e.preventDefault()
      submit()
    }}
  >
    {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}

    <FormSection title="Details">
      <ToggleField bind:checked={$form.feed.active} label="Active" name="active" error={errorFor('active')} />
      <ToggleField
        bind:checked={$form.feed.manual_ranking_enabled}
        label="Manual Ranking"
        name="manual_ranking_enabled"
        help="When enabled, content is manually ordered by its position value."
        error={errorFor('manual_ranking_enabled')}
      />
      <TextField bind:value={$form.feed.name} label="Name" name="name" error={errorFor('name')} required />
      <TextField
        bind:value={$form.feed.display_name}
        label="Display Name"
        name="display_name"
        help="Displayed in the app"
        error={errorFor('display_name')}
        required
      />
      <SelectField
        bind:value={$form.feed.feed_type}
        label="Feed Type"
        name="feed_type"
        options={options.feed_type_options ?? []}
        help="Towns can only have 1 of each non-standard type"
        error={errorFor('feed_type')}
        onchange={refreshRules}
        required
      />
      <SelectField
        bind:value={$form.feed.area_id}
        label="College Town"
        name="area_id"
        options={options.area_options ?? []}
        error={errorFor('area_id')}
        onchange={refreshRules}
        required
      />
    </FormSection>

    {#if form_rules.show_targeting}
      <FormSection title="Targeting">
        {#if form_rules.show_multiplier}
          <SelectField
            bind:value={$form.feed.multiplier}
            label="Multiplier Amount"
            name="multiplier"
            options={options.multiplier_options ?? []}
            error={errorFor('multiplier')}
            required
          />
        {/if}

        <fieldset class="fieldset">
          <legend class="fieldset-legend">Impacted Places</legend>
          <div class="flex gap-4">
            {#each form_rules.impacted_places_options ?? [] as option (option.value)}
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  class="radio radio-sm"
                  name="impacted_places"
                  value={option.value}
                  disabled={option.disabled}
                  checked={$form.feed.impacted_places === option.value}
                  onchange={() => ($form.feed.impacted_places = option.value)}
                />
                {option.label}
              </label>
            {/each}
          </div>
        </fieldset>

        {#if $form.feed.impacted_places === 'impacted_places_custom'}
          <MultiSelect bind:value={$form.feed.category_ids} options={options.category_options ?? []} label="Categories" placeholder="Select a category…" />
          {#if errorFor('category_ids')}<p class="text-sm text-error">{errorFor('category_ids')}</p>{/if}
          <MultiSelect bind:value={$form.feed.sub_category_ids} options={options.sub_category_options ?? []} label="Sub-categories" placeholder="Select a sub category…" />
          {#if errorFor('sub_category_ids')}<p class="text-sm text-error">{errorFor('sub_category_ids')}</p>{/if}
          <MultiSelect bind:value={$form.feed.place_ids} source="places" sourceParams={{ area_id: $form.feed.area_id }} label="Places" placeholder="Select a place…" />
          {#if errorFor('place_ids')}<p class="text-sm text-error">{errorFor('place_ids')}</p>{/if}
        {/if}
      </FormSection>
    {/if}

    <FormSection title="Audiences">
      <fieldset class="fieldset">
        <div class="flex gap-4 mb-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" class="radio radio-sm" checked={audienceType === 'everyone'} onclick={() => setAudienceType('everyone')} />
            Everyone
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" class="radio radio-sm" checked={audienceType === 'custom'} onclick={() => setAudienceType('custom')} />
            Custom
          </label>
        </div>

        {#if audienceType === 'custom'}
          <MultiSelect
            bind:value={$form.feed.audience_names}
            options={options.audience_options ?? []}
            label="Audience"
            placeholder="Search audiences…"
          />
          {#if errorFor('audience_names')}<p class="text-sm text-error mt-1">{errorFor('audience_names')}</p>{/if}

          {#if hasCsv}
            <div class="flex items-center gap-2 flex-wrap mt-2">
              <FileButton size="sm" icon="add" accept="text/csv,.csv" onchange={pickCsv}>Import from CSV</FileButton>
              <Button variant="default" size="sm" onclick={clearUsers}>Clear Users</Button>
              <Button variant="default" size="sm" disabled={$form.feed.user_ids.length === 0} onclick={() => (usersOpen = true)}>View Users</Button>
              <span class="text-sm text-base-content/60">{$form.feed.user_ids.length} users included.</span>
            </div>
            {#if csv.summary}<p class="text-sm text-base-content/60 mt-1">{csv.summary}</p>{/if}
            {#if csv.error}<p class="text-sm text-error mt-1">{csv.error}</p>{/if}
          {/if}
        {/if}
        {#if errorFor('user_ids')}<p class="text-sm text-error mt-1">{errorFor('user_ids')}</p>{/if}
      </fieldset>
    </FormSection>

    <FormSection title="Schedule">
      <ScheduleEditor bind:value={$form.feed.schedule_attributes} timeZones={options.time_zones ?? []} error={errorFor('schedule_attributes')} />
    </FormSection>

    {#snippet footer()}
      <Button href={back_url} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} label="Save" full={false} />
    {/snippet}
  </FormLayout>

  <AudienceUserList bind:open={usersOpen} userIds={$form.feed.user_ids} />
</Layout>
